import { useState, useEffect, useCallback, useRef } from 'react'
import { Hub } from 'aws-amplify/utils'
import { adminAuthService } from '../services/adminAuth'
import { adminService } from '../services/api'
import type { Admin } from '../types'

interface AdminAuthState {
  admin: Admin | null
  loading: boolean
  error: string | null
  isAuthenticated: boolean
}

let cachedAdminState: AdminAuthState | null = null
let isInitialLoad = true

export const useAdminAuth = () => {
  const [state, setState] = useState<AdminAuthState>(() => {
    if (cachedAdminState && !isInitialLoad) {
      return cachedAdminState
    }
    return {
      admin: null,
      loading: true,
      error: null,
      isAuthenticated: false
    }
  })

  const loadingRef = useRef(false)
  const isMountedRef = useRef(true)
  const sessionCheckInterval = useRef<NodeJS.Timeout>()
  const hasLoadedRef = useRef(false)

  const updateState = useCallback((updates: Partial<AdminAuthState>) => {
    if (isMountedRef.current) {
      setState(prev => {
        const newState = { ...prev, ...updates }
        cachedAdminState = newState
        return newState
      })
    }
  }, [])

  const verifyAdminStatus = useCallback(async (admin: Admin): Promise<boolean> => {
    if (admin.status !== 'ACTIVE') {
      return false
    }

    try {
      const currentAdmin = await adminService.get(admin.id)
      return currentAdmin?.status === 'ACTIVE'
    } catch (error) {
      console.error('Admin status verification failed:', error)
      return false
    }
  }, [])

  const loadAdmin = useCallback(async (force = false) => {
    if (loadingRef.current) return
    if (!force && hasLoadedRef.current && cachedAdminState?.admin) return
    
    try {
      loadingRef.current = true
      updateState({ loading: true, error: null })
      
      const currentUser = await adminAuthService.getCurrentAdmin()
      
      if (!currentUser) {
        updateState({ 
          admin: null, 
          loading: false, 
          isAuthenticated: false 
        })
        hasLoadedRef.current = true
        return
      }

      const userEmail = currentUser.signInDetails?.loginId || currentUser.username

      const admin = await adminService.findByEmail(userEmail)
      
      if (!admin) {
        updateState({ 
          admin: null, 
          loading: false, 
          isAuthenticated: false,
          error: 'Admin account not found'
        })
        hasLoadedRef.current = true
        return
      }

      const isValidStatus = await verifyAdminStatus(admin)
      
      if (isValidStatus) {
        updateState({
          admin,
          loading: false,
          isAuthenticated: true
        })
        hasLoadedRef.current = true
      } else {
        await adminAuthService.signOut()
        updateState({ 
          admin: null, 
          loading: false, 
          isAuthenticated: false,
          error: 'Account is not active'
        })
        hasLoadedRef.current = true
      }
    } catch (error: any) {
      console.error('Load admin failed:', error)
      
      const errorMessage = error?.errors?.[0]?.message || error?.message || 'Failed to load admin'
      
      updateState({ 
        admin: null, 
        loading: false, 
        error: errorMessage, 
        isAuthenticated: false 
      })
      hasLoadedRef.current = true
    } finally {
      loadingRef.current = false
      isInitialLoad = false
    }
  }, [updateState, verifyAdminStatus])

  const startSessionCheck = useCallback(() => {
    if (sessionCheckInterval.current) {
      clearInterval(sessionCheckInterval.current)
    }

    sessionCheckInterval.current = setInterval(async () => {
      try {
        const session = await adminAuthService.getSession()
        if (!session || !session.tokens) {
          updateState({
            admin: null,
            loading: false,
            isAuthenticated: false,
            error: 'Session expired'
          })
          if (sessionCheckInterval.current) {
            clearInterval(sessionCheckInterval.current)
          }
          hasLoadedRef.current = false
          cachedAdminState = null
        }
      } catch (error) {
        console.error('Session check failed:', error)
      }
    }, 60000)
  }, [updateState])

  useEffect(() => {
    isMountedRef.current = true
    
    if (!hasLoadedRef.current || !cachedAdminState?.admin) {
      loadAdmin()
    }

    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      switch (payload.event) {
        case 'signedIn':
          hasLoadedRef.current = false
          cachedAdminState = null
          loadAdmin(true)
          startSessionCheck()
          break
        case 'signedOut':
          if (sessionCheckInterval.current) {
            clearInterval(sessionCheckInterval.current)
          }
          hasLoadedRef.current = false
          cachedAdminState = null
          updateState({ 
            admin: null, 
            loading: false, 
            isAuthenticated: false 
          })
          break
        case 'tokenRefresh':
          break
        case 'tokenRefresh_failure':
          if (sessionCheckInterval.current) {
            clearInterval(sessionCheckInterval.current)
          }
          hasLoadedRef.current = false
          cachedAdminState = null
          updateState({
            admin: null,
            loading: false,
            isAuthenticated: false,
            error: 'Session expired'
          })
          break
      }
    })

    return () => {
      isMountedRef.current = false
      if (sessionCheckInterval.current) {
        clearInterval(sessionCheckInterval.current)
      }
      unsubscribe()
    }
  }, [loadAdmin, updateState, startSessionCheck])

  const signIn = async (email: string, password: string) => {
    try {
      updateState({ loading: true, error: null })
      
      const result = await adminAuthService.signIn(email, password)
      
      if (result.isSignedIn) {
        hasLoadedRef.current = false
        cachedAdminState = null
        await loadAdmin(true)
        startSessionCheck()
        return { success: true }
      } else {
        updateState({ loading: false, error: 'Authentication required' })
        return { success: false, error: 'Authentication required' }
      }
    } catch (err: any) {
      const errorMessage = err.message || 'Sign in failed'
      updateState({ loading: false, error: errorMessage })
      return { success: false, error: errorMessage }
    }
  }

  const signOut = async () => {
    try {
      if (sessionCheckInterval.current) {
        clearInterval(sessionCheckInterval.current)
      }
      await adminAuthService.signOut()
      hasLoadedRef.current = false
      cachedAdminState = null
      updateState({ 
        admin: null, 
        loading: false, 
        isAuthenticated: false 
      })
      return { success: true }
    } catch (err: any) {
      const errorMessage = err.message || 'Sign out failed'
      return { success: false, error: errorMessage }
    }
  }

  return {
    ...state,
    signIn,
    signOut,
    refreshAdmin: () => loadAdmin(true)
  }
}